import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { WithdrawalFilters } from "../../types/withdrawal";

interface WithdrawalFilterProps {
  filters: WithdrawalFilters;
  setFilters: (filters: WithdrawalFilters) => void;
  onClose: () => void;
}

export const WithdrawalFilter: React.FC<WithdrawalFilterProps> = ({
  filters,
  setFilters,
  onClose,
}) => {
  const [localFilters, setLocalFilters] = useState<WithdrawalFilters>(filters);
  const [show, setShow] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setShow(true);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleApply = () => {
    setFilters(localFilters);

    const newParams = new URLSearchParams(searchParams);

    if (localFilters.withdrawalTimeFrom) {
      newParams.set("dateFrom", localFilters.withdrawalTimeFrom);
    } else {
      newParams.delete("dateFrom");
    }

    if (localFilters.withdrawalTimeTo) {
      newParams.set("dateTo", localFilters.withdrawalTimeTo);
    } else {
      newParams.delete("dateTo");
    }

    newParams.set("page", "1");
    setSearchParams(newParams);

    handleClose();
  };

  const handleClear = () => {
    const cleared: WithdrawalFilters = {
      withdrawalTimeFrom: undefined,
      withdrawalTimeTo: undefined,
    };
    setLocalFilters(cleared);
    setFilters(cleared);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("dateFrom");
    newParams.delete("dateTo");
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleClose = () => {
    setShow(false);
    timerRef.current = setTimeout(() => {
      onClose();
    }, 300);
  };

  const activeFilterCount =
    (localFilters.withdrawalTimeFrom ? 1 : 0) +
    (localFilters.withdrawalTimeTo ? 1 : 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div
        className={`
          w-full sm:max-w-md sm:rounded-lg sm:mx-auto
          bg-white dark:bg-[#1f1f1f]
          p-6 rounded-t-2xl max-h-[85vh] overflow-y-auto shadow-lg border border-gray-700
          transform transition-all duration-300 ease-out
          ${
            show
              ? "opacity-100 translate-y-0 sm:scale-100"
              : "opacity-0 translate-y-full sm:scale-95"
          }
        `}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Filters ({activeFilterCount})
          </h2>
          <button onClick={handleClose}>
            <X
              className="text-gray-500 hover:text-black dark:hover:text-white"
              size={20}
            />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-300">
              Date Range
            </label>
            <input
              type="date"
              value={localFilters.withdrawalTimeFrom || ""}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  withdrawalTimeFrom: e.target.value,
                })
              }
              className="mt-1 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
            />
            <input
              type="date"
              value={localFilters.withdrawalTimeTo || ""}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  withdrawalTimeTo: e.target.value,
                })
              }
              className="mt-2 w-full bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded px-3 py-2"
            />
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              onClick={handleClear}
              className="px-4 py-2 rounded bg-gray-300 dark:bg-gray-700 text-black dark:text-white"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
